import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import Jobs from './pages/Jobs';
import Login from './pages/Login';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { isLoading } = useAuth();
  const { accounts, inProgress } = useMsal();
  const [waitingForAuth, setWaitingForAuth] = useState(true);
  
  useEffect(() => {
    // Force a longer wait for auth to initialize fully
    const timer = setTimeout(() => {
      setWaitingForAuth(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Show loading indicator while auth is initializing or in progress
  if (isLoading || waitingForAuth || inProgress !== "none") {
    console.log('ProtectedRoute - showing loading spinner', { isLoading, waitingForAuth, inProgress, hasAccounts: accounts.length > 0 });
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  // Force auth to pass if we have accounts, regardless of isAuthenticated flag
  // This helps with MSAL race conditions
  if (accounts.length > 0) {
    console.log('ProtectedRoute - accounts exist, treating as authenticated');
    return children;
  }
  
  // Only redirect if we're sure authentication has failed (not loading)
  if (!isAuthenticated) {
    console.log('Redirecting to login - no accounts and not authenticated');
    return <Navigate to="/login" />;
  }
  
  return children;
};

const ErrorBoundary = () => {
  const { error } = useMsal();
  if (error) {
    console.error("MSAL Error:", error);
    return <div>An authentication error occurred: {error.errorMessage}</div>;
  }
  return null;
};

// AuthRedirectController component to handle login redirects
const AuthRedirectController = () => {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectChecked, setRedirectChecked] = useState(false);

  useEffect(() => {
    // Run this only once
    if (!redirectChecked) {
      console.log('AuthRedirectController - Checking redirect:', { 
        path: location.pathname,
        accountsExist: accounts.length > 0 
      });
      
      // If we're at /login but have accounts, redirect to home
      if (location.pathname === '/login' && accounts.length > 0) {
        console.log('AuthRedirectController - Redirecting from /login to / because accounts exist');
        navigate('/', { replace: true });
      }
      
      setRedirectChecked(true);
    }
  }, [accounts, location.pathname, navigate, redirectChecked]);

  // This is a controller component that doesn't render anything
  return null;
};

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary />
      <AuthRedirectController />
      <Routes>
        <Route path="/login" element={
          <UnauthenticatedTemplate>
            <Login />
          </UnauthenticatedTemplate>
        } />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <AuthenticatedTemplate>
                <Layout />
              </AuthenticatedTemplate>
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="upload" element={<Upload />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/:status" element={<Jobs />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;